/* ============================================================
   td-sticker-sample-blue.js — Business Sample Pack, "Branded Blue"
   (/pages/sample-pack). Binds to [data-stk-blue]. Standalone.

   2026-08-29 — THE PACK PIVOT. The order is now a 3-size pack (2"/3"/4"), not ten
   3" stickers. Two things to know before editing submit():
     • qty comes from sample_offer.sample_pack.qty (3). sample_offer.qty is STILL 10
       and must stay — the bridge accepts both so it could deploy ahead of the theme.
     • spec.size stays medium_3in, so /pages/reorder reprices a 3" spec unchanged.
       The three printed sizes are stamped server-side from config as _Pack Sizes.
   ADDED: a REQUIRED business URL (spec.business_url → the bridge's _Business URL),
   which is the second CTA blocker — see refreshCta(), which must name whichever one
   is actually blocking or the customer cannot tell what the page wants.

   Forked from td-sticker-sample.js (2026-07-25). What changed vs. the fork:
     PORTED (behavior-identical): the upload pipeline + in-flight/failure states,
       sessionStorage artwork persistence, the config fetch off data-config-url,
       optional proof_email capture + loose validation + email_optin, the disabled-
       until-upload CTA gate, finalizeAndPost()/submit(), and bridge-error surfacing
       into the role="alert" node next to the CTA.
     REMOVED: the preview engine + the Step-2 preview modal, and the 50/100 upgrade
       offer + its modal (all dead on the live page). td-sticker-preview.js is NOT
       loaded here, so the Cloudinary upload is inlined below (it used to come from
       that engine).
     ADDED: a two-option SHIPPING selector (Tracked / Standard mail). The browser
       sends an OPTION KEY (spec.shipping_option); the bridge resolves the price
       from config. Prices are rendered from config, never trusted from the client.
     ADDED 2026-08-07: FINISH (Matte / Gloss) + SHAPE (Custom Die-Cut / Circle /
       Oval / Square / Rectangle / Surprise Me) pills under the dropzone. Order-only
       spec — no price effect on this path, no preview to drive. They are seeded from
       sample_offer CONFIG so a default change moves the UI, and they persist into the
       artwork sessionStorage blob so a reload keeps the picks.

   SPEC FIELDS STILL SENT (the order spec must NOT shrink even though the preview
   UI is gone): Shape / Cut Type / Border Type / Material are line-item properties
   Trello / the packing slip / the proof engine all read. Cut Type (die_cut) and
   Border Type (regular) still come from sample_offer CONFIG for every free pack;
   Shape and Material are now the customer's pick, config-seeded. variant_id is
   REQUIRED (Klaviyo Reviews).
   ============================================================ */
(function () {
  'use strict';

  var SS_KEY = 'stkBlue.artwork'; // distinct from the old section's key — the two never cross-restore

  // Inlined Cloudinary uploader (was window.TDStickerPreview.uploadArtwork; that
  // engine is intentionally not loaded on this page). Anonymous unsigned upload —
  // the same cloud / preset / folder the order-time capture has always used.
  var CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dsbz1mtmr/image/upload';
  var CLOUDINARY_PRESET = 'tapedudes_unsigned';
  /* ── SVG normalisation ────────────────────────────────────────────────
     Cloudinary sniffs an SVG's format from its FIRST BYTES. A file that does
     not begin with an XML declaration sniffs as `raw`, and the unsigned preset
     rejects it: 400 "Raw file format svg not allowed" — measured 2026-08-31 on
     auto/upload, image/upload and raw/upload alike. Illustrator "Export As" and
     Figma both write SVGs without the declaration. Order #2869 lost its artwork
     this way. A BOM is tolerated but leading whitespace is NOT, so the
     declaration has to end up at byte 0.
     Fails open: any unexpected file, or any read error, uploads unchanged. */
  var TD_XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n';

  function normalizeSvg(file) {
    var name = file && file.name ? String(file.name).toLowerCase() : '';
    var looksSvg = !!file && (file.type === 'image/svg+xml' || /\.svg$/.test(name));
    if (!looksSvg || !file.slice || !file.slice(0, 1).arrayBuffer) return Promise.resolve(file);

    return file.slice(0, 1024).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), i = 0;
      if (b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) i = 3;                 // BOM
      while (i < b.length && (b[i] === 0x20 || b[i] === 0x09 || b[i] === 0x0A || b[i] === 0x0D)) i++;
      var head = new TextDecoder().decode(b.subarray(i));

      // Already declared and already at byte 0 — leave the bytes alone.
      if (/^<\?xml[\s?]/.test(head)) return i === 0 ? file : new File([file.slice(i)], file.name, { type: 'image/svg+xml' });
      // Named .svg but not SVG markup — never rewrite it, that would corrupt it.
      if (!/<svg[\s>]/i.test(head)) return file;
      return new File([TD_XML_DECL, file.slice(i)], file.name, { type: 'image/svg+xml' });
    })['catch'](function () { return file; });
  }

  function uploadToCloudinary(file, folder) {
    return normalizeSvg(file).then(function (f) {
      var fd = new FormData();
      fd.append('file', f);
      fd.append('upload_preset', CLOUDINARY_PRESET);
      if (folder) fd.append('folder', folder);
      return fetch(CLOUDINARY_URL, { method: 'POST', body: fd })
        .then(function (res) { if (!res.ok) throw new Error('upload HTTP ' + res.status); return res.json(); })
        .then(function (j) { if (!j || !j.public_id) throw new Error('no public_id'); return { public_id: j.public_id, secure_url: j.secure_url }; });
    });
  }

  // Summary-row label per shipping key (the radio TITLE carries the price; the
  // summary/bar carry a plainer label). Kept in sync with the config keys.
  var SHIP_SUMMARY_LABEL = { tracked: 'Tracked shipping', standard: 'Standard mail (no tracking)' };

  function money(n) { return '$' + Number(n).toFixed(2); }

  function initBlue(root) {
    if (!root || root.dataset.stkBlueReady === '1') return;
    root.dataset.stkBlueReady = '1';

    var configUrl = root.dataset.configUrl;
    var endpoint = root.dataset.checkoutEndpoint;
    var nonce = root.dataset.signingNonce;
    var variantId = root.dataset.variantId;

    // ── refs ──
    var fileInput = root.querySelector('[data-stk-blue-file]');
    var dropzone = root.querySelector('[data-stk-blue-dropzone]');
    var dzEmpty = root.querySelector('[data-stk-blue-dz-empty]');
    var dzFilled = root.querySelector('[data-stk-blue-dz-filled]');
    var thumbEl = root.querySelector('[data-stk-blue-thumb]');
    var filenameEl = root.querySelector('[data-stk-blue-filename]');
    var receivedEl = root.querySelector('[data-stk-blue-received]');
    var emailEl = root.querySelector('[data-stk-blue-email]');
    var bizEl = root.querySelector('[data-stk-blue-business-url]');
    var notesEl = root.querySelector('[data-stk-blue-notes]');
    var shipInputs = Array.prototype.slice.call(root.querySelectorAll('[data-stk-blue-ship-input]'));
    var specRoot = root.querySelector('[data-stk-blue-spec]');
    var cta = root.querySelector('[data-stk-blue-cta]');
    var ctaError = root.querySelector('[data-stk-blue-cta-error]');

    // summary hooks
    var shipLabelEl = root.querySelector('[data-stk-blue-ship-label]');
    var shipCostEl = root.querySelector('[data-stk-blue-ship-cost]');
    var totalEl = root.querySelector('[data-stk-blue-total]');
    var barLabelEl = root.querySelector('[data-stk-blue-bar-label]');
    var barTotalEl = root.querySelector('[data-stk-blue-bar-total]');

    if (!fileInput || !cta) return;

    var state = {
      config: null,
      artwork_filename: '',
      artwork_file: null,
      artwork_url: '',
      artwork_public_id: '',
      objectUrl: null,           // local preview URL — revoked on replace
      uploadInFlight: false,
      uploadFailed: false,
      notes: '',
      proof_email: '',           // OPTIONAL pre-checkout capture → spec.proof_email + Klaviyo "Design Saved"
      business_url: '',          // REQUIRED — gates the CTA; lands as the bridge's _Business URL
      finish: 'matte',           // matte | gloss — seeded from config, overwritten by the pills
      shape: 'custom_shape',     // custom_shape | circle | oval | square | rectangle | surprise
      shipping_option: 'tracked',// overwritten from config default on load
      shipOptions: {},           // key → { title, price } from config
      submitting: false
    };

    // ── sessionStorage persistence (survives a refresh) ──
    // Finish + Shape ride the SAME blob as the artwork, so a customer who reloads
    // after uploading keeps their picks along with their file. Blobs written before
    // the pills shipped simply carry neither key — restore skips them and config
    // seeds the defaults, so old sessions degrade to today's behavior.
    function persistArtwork() {
      try {
        if (state.artwork_url) {
          sessionStorage.setItem(SS_KEY, JSON.stringify({
            url: state.artwork_url, public_id: state.artwork_public_id, filename: state.artwork_filename,
            finish: state.finish, shape: state.shape
          }));
        }
      } catch (e) { /* private mode / quota — non-fatal */ }
    }
    function readPersistedArtwork() {
      try { var raw = sessionStorage.getItem(SS_KEY); if (!raw) return null; var o = JSON.parse(raw); if (o && o.url) return o; } catch (e) {}
      return null;
    }
    function clearPersistedArtwork() { try { sessionStorage.removeItem(SS_KEY); } catch (e) {} }

    // ── inputs ──
    if (notesEl) notesEl.addEventListener('input', function () { state.notes = notesEl.value; });
    if (emailEl) emailEl.addEventListener('input', function () { state.proof_email = emailEl.value; });
    // Required field — it gates the CTA, so every keystroke has to re-evaluate the
    // button (the email above does not, because it is optional and gates nothing).
    if (bizEl) bizEl.addEventListener('input', function () {
      state.business_url = bizEl.value;
      refreshCta();
    });

    // ── shipping selector ──
    shipInputs.forEach(function (input) {
      input.addEventListener('change', function () { if (input.checked) selectShipping(input.value); });
    });
    function selectShipping(key) {
      if (!state.shipOptions[key]) key = state.shipping_option; // ignore unknown
      state.shipping_option = key;
      Array.prototype.slice.call(root.querySelectorAll('[data-stk-blue-ship]')).forEach(function (label) {
        label.classList.toggle('is-selected', label.getAttribute('data-stk-blue-ship') === key);
      });
      renderSummary();
      refreshCta();
    }

    // ── Finish + Shape pills ──
    // Order-only spec, no price effect: computeStickerSamplePrice() validates
    // product_type + qty and returns $0 regardless of either, and shipping is
    // resolved by KEY from config. Gloss and Circle cost the customer exactly what
    // Matte and Custom Die-Cut do. Both rows always hold a valid selection, so they
    // can never gate the CTA — a successful upload stays the only gate.
    var specFromSession = false; // a restored session's picks beat the config defaults

    function pillsIn(attr) {
      return specRoot ? Array.prototype.slice.call(specRoot.querySelectorAll('[' + attr + ']')) : [];
    }

    // Reflect state onto one row. Roving tabindex (only the selected pill is
    // tabbable) is what makes Tab land on the row and the arrow keys move within it —
    // these are <button role="radio">, so none of that comes for free.
    function reflectRow(attr, value) {
      pillsIn(attr).forEach(function (btn) {
        var on = btn.getAttribute(attr) === value;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.tabIndex = on ? 0 : -1;
      });
    }
    function reflectSpec() {
      reflectRow('data-stk-blue-finish', state.finish);
      reflectRow('data-stk-blue-shape', state.shape);
    }

    function selectSpec(attr, key, value, focusBtn) {
      var known = pillsIn(attr).some(function (b) { return b.getAttribute(attr) === value; });
      if (!known) return;                 // ignore anything not on the row
      state[key] = value;
      reflectSpec();
      persistArtwork();                   // no-op until an upload exists
      if (focusBtn) {
        var next = pillsIn(attr).filter(function (b) { return b.getAttribute(attr) === value; })[0];
        if (next) next.focus();
      }
    }

    if (specRoot) {
      specRoot.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('.stk-blue__pill') : null;
        if (!btn || !specRoot.contains(btn)) return;
        if (btn.hasAttribute('data-stk-blue-finish')) selectSpec('data-stk-blue-finish', 'finish', btn.getAttribute('data-stk-blue-finish'), false);
        else if (btn.hasAttribute('data-stk-blue-shape')) selectSpec('data-stk-blue-shape', 'shape', btn.getAttribute('data-stk-blue-shape'), false);
      });

      // Arrow keys move the selection within the focused row (wrapping), Home/End
      // jump to its ends — the WAI-ARIA radiogroup pattern.
      specRoot.addEventListener('keydown', function (e) {
        var btn = e.target.closest ? e.target.closest('.stk-blue__pill') : null;
        if (!btn || !specRoot.contains(btn)) return;
        var attr = btn.hasAttribute('data-stk-blue-finish') ? 'data-stk-blue-finish'
                 : btn.hasAttribute('data-stk-blue-shape') ? 'data-stk-blue-shape' : null;
        if (!attr) return;
        var key = attr === 'data-stk-blue-finish' ? 'finish' : 'shape';
        var row = pillsIn(attr);
        var i = row.indexOf(btn);
        if (i < 0) return;
        var j;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % row.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + row.length) % row.length;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = row.length - 1;
        else return;
        e.preventDefault();
        selectSpec(attr, key, row[j].getAttribute(attr), true);
      });
    }

    // ── config → shipping prices + summary ──
    if (configUrl) {
      fetch(configUrl)
        .then(function (r) { return r.json(); })
        .then(function (cfg) {
          state.config = cfg;
          // Seed the pills from CONFIG, not from the markup's is-selected class — a
          // sample_offer default change must move the UI rather than fight it. A
          // restored session's own picks win (they were an explicit choice).
          var sample = cfg && cfg.sample_offer;
          if (sample && !specFromSession) {
            if (sample.finish) state.finish = sample.finish;
            if (sample.shape) state.shape = sample.shape;
            reflectSpec();
          }
          var so = cfg && cfg.sample_offer && cfg.sample_offer.shipping_options;
          if (so && Array.isArray(so.options)) {
            so.options.forEach(function (o) {
              if (!o || !o.key) return;
              state.shipOptions[o.key] = { title: o.title, price: Number(o.price) };
              // Render each option's price from config (never hardcoded as truth).
              var priceEl = root.querySelector('[data-stk-blue-price="' + o.key + '"]');
              if (priceEl) priceEl.textContent = money(o.price);
            });
            if (so.default && state.shipOptions[so.default]) selectShipping(so.default);
          }
          renderSummary();
          refreshCta();
        })
        .catch(function (err) { console.warn('[stk-blue] config fetch failed', err); });
    }

    function selectedPrice() {
      var o = state.shipOptions[state.shipping_option];
      return o && isFinite(o.price) ? o.price : 4.95; // markup-default fallback
    }

    function renderSummary() {
      var cost = money(selectedPrice());
      var label = SHIP_SUMMARY_LABEL[state.shipping_option] || 'Shipping';
      if (shipLabelEl) shipLabelEl.textContent = label;
      if (shipCostEl) shipCostEl.textContent = cost;
      if (totalEl) totalEl.textContent = cost;   // stickers are free → total = shipping
      if (barLabelEl) barLabelEl.textContent = label;
      if (barTotalEl) barTotalEl.textContent = cost;
    }

    // ── upload UI ──
    function showEmpty() { if (dzEmpty) dzEmpty.hidden = false; if (dzFilled) dzFilled.hidden = true; }
    function showFilled() { if (dzEmpty) dzEmpty.hidden = true; if (dzFilled) dzFilled.hidden = false; }
    function setReceived(kind) {
      if (!receivedEl) return;
      var map = {
        uploading: 'Uploading your logo…',
        done: '✓ You\'ll receive a design proof before we print. Unlimited revisions.',
        failed: 'Upload failed — tap Replace file and try again.'
      };
      receivedEl.textContent = map[kind] || map.done;
      receivedEl.classList.toggle('is-failed', kind === 'failed');
    }
    function setThumb(src) {
      if (!thumbEl) return;
      thumbEl.style.backgroundImage = src ? 'url("' + src + '")' : '';
    }

    // ── upload → local thumbnail + immediate Cloudinary upload + persist ──
    fileInput.addEventListener('change', function () {
      var f = fileInput.files && fileInput.files[0];
      if (f) handleFile(f);
    });

    function handleFile(file) {
      if (state.objectUrl) { try { URL.revokeObjectURL(state.objectUrl); } catch (e) {} state.objectUrl = null; }
      state.artwork_filename = file.name;
      state.artwork_file = file;
      state.artwork_url = '';
      state.artwork_public_id = '';
      state.uploadFailed = false;
      state.uploadInFlight = true;

      // Immediate local thumbnail (don't wait for Cloudinary).
      try { state.objectUrl = URL.createObjectURL(file); setThumb(state.objectUrl); } catch (e) { setThumb(''); }
      if (filenameEl) filenameEl.textContent = file.name;
      setReceived('uploading');
      showFilled();
      refreshCta();

      uploadToCloudinary(file, 'orders')
        .then(function (up) {
          state.artwork_url = up.secure_url;
          state.artwork_public_id = up.public_id;
          state.uploadInFlight = false;
          persistArtwork();
          setReceived('done');
          refreshCta();
        })
        .catch(function (err) {
          console.warn('[stk-blue] artwork upload failed', err);
          state.uploadInFlight = false;
          state.uploadFailed = true;
          setReceived('failed');
          refreshCta();
        });
    }

    // ── drag & drop (click-to-browse is native via the <label>) ──
    if (dropzone) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) { e.preventDefault(); e.stopPropagation(); dropzone.classList.add('is-dragover'); });
      });
      ['dragleave', 'dragend'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) { e.preventDefault(); e.stopPropagation(); dropzone.classList.remove('is-dragover'); });
      });
      dropzone.addEventListener('drop', function (e) {
        e.preventDefault(); e.stopPropagation(); dropzone.classList.remove('is-dragover');
        var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) handleFile(f);
      });
    }

    // ── restore a prior upload (survives a refresh) ──
    var persisted = readPersistedArtwork();
    if (persisted) {
      state.artwork_filename = persisted.filename || 'your logo';
      state.artwork_url = persisted.url;
      state.artwork_public_id = persisted.public_id || '';
      if (persisted.finish) { state.finish = persisted.finish; specFromSession = true; }
      if (persisted.shape) { state.shape = persisted.shape; specFromSession = true; }
      if (filenameEl) filenameEl.textContent = state.artwork_filename;
      setThumb(state.artwork_url); // hosted Cloudinary image (no File in memory)
      setReceived('done');
      showFilled();
    }

    reflectSpec();
    refreshCta();

    // ── CTA state ──
    // The business URL is required as of 2026-08-29, so the CTA now has TWO possible
    // blockers. The label must name the one that is actually blocking: a customer who
    // has uploaded their logo and is staring at "Upload your logo to continue" has no
    // way to tell what the page wants. Artwork is reported first because it is the
    // bigger, more visible step; the URL only surfaces once the file is in.
    function hasBusinessUrl() { return !!(state.business_url || '').trim(); }

    function refreshCta() {
      var ready = !!state.artwork_url && hasBusinessUrl() && !state.submitting;
      cta.disabled = !ready;
      if (!state.submitting) {
        var label;
        if (!state.artwork_filename) label = 'Upload your logo to continue';
        else if (state.uploadInFlight) label = 'Uploading your logo…';
        else if (state.uploadFailed || !state.artwork_url) label = 'Re-upload your logo';
        else if (!hasBusinessUrl()) label = 'Add your website to continue';
        else label = 'Send my free pack — ' + money(selectedPrice());
        cta.textContent = label;
      }
    }

    cta.addEventListener('click', function () {
      if (cta.disabled || state.submitting) return;
      proceed(submit, cta, 'Creating checkout…');
    });

    function proceed(fn, btn, busyLabel) {
      state.submitting = true;
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = busyLabel;
      if (ctaError) ctaError.textContent = '';
      fn().catch(function (err) {
        console.error('[stk-blue] submit failed', err);
        if (ctaError) ctaError.textContent = String((err && err.message) || 'Could not start checkout — please try again');
        state.submitting = false;
        btn.disabled = false;
        btn.textContent = original;
        refreshCta();
      });
    }

    // ── submit — the 3-size pack. Shape / Cut Type / Border Type / Material come from
    // CONFIG (not removed UI), so the order spec stays complete. shipping_option
    // is the KEY; the bridge resolves the price. ──
    function submit() {
      var cfg = state.config, sample = cfg && cfg.sample_offer;
      if (!sample) return Promise.reject(new Error('Sample unavailable — please reload the page'));
      // The business sample pack (2026-08-29). qty comes from sample_offer.sample_pack,
      // NOT sample_offer.qty — the latter is still 10 and is deliberately left there for
      // the bridge's legacy branch. Fails closed rather than falling back to 10: a
      // fallback would silently ship ten 3" stickers to someone the page just promised
      // three sizes, which is a wrong product nobody would notice. A rejection is loud.
      var pack = sample.sample_pack;
      if (!pack || !pack.qty) return Promise.reject(new Error('Sample unavailable — please reload the page'));
      return finalizeAndPost({
        sample_offer: true,
        sample_pack: true,
        product_type: sample.product_type,   // vinyl
        size: sample.size,                    // medium_3in
        // "Surprise Me" posts custom_shape — spec.shape is a load-bearing enum the
        // packing slip prints and the proof engine snaps the cutline off, so an
        // unknown kind must never reach it. The intent rides in shape_request
        // instead → the bridge's _Shape Request property (Admin-only, off the slip).
        shape: (state.shape === 'surprise' ? 'custom_shape' : state.shape),
        shape_request: (state.shape === 'surprise' ? 'Surprise me — designer\'s pick' : ''),
        cut_type: sample.cut_type,            // die_cut — every shape, unchanged
        border_type: sample.border_type,      // regular — unchanged
        material: state.finish,               // matte | gloss (customer's pick)
        // The order's canonical Size stays medium_3in (from sample.size above) so
        // /pages/reorder reprices a 3" spec unchanged; the three printed sizes travel
        // as the bridge's _Pack Sizes property, resolved from config on the server.
        qty: Number(pack.qty),                // 3 — the number PROMISED
        business_url: (state.business_url || '').trim(),
        shipping_option: state.shipping_option, // tracked | standard (bridge resolves price)
        artwork_filename: state.artwork_filename,
        notes: state.notes
      });
    }

    // Artwork was uploaded at select-time (or restored from sessionStorage). Submit
    // REUSES the known URL and never re-uploads; fails closed if no URL exists. No
    // identity token — the customer is created by the checkout.
    function finalizeAndPost(spec) {
      if (!state.artwork_url) {
        return Promise.reject(new Error('We lost your uploaded logo — tap Replace file and upload it again.'));
      }
      spec.artwork_url = state.artwork_url;
      spec.artwork_public_id = state.artwork_public_id;

      // Optional pre-checkout email (value exchange, not a gate): trim + loose-validate
      // and attach only when it looks like an email. The bridge stamps it on the draft
      // (order email) + fires the "Design Saved" recovery event; email_optin drives the
      // "Design Leads — no order" subscribe (disclosed opt-in on this page).
      var pe = (state.proof_email || '').trim();
      if (pe && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pe)) {
        spec.proof_email = pe;
        spec.email_optin = true;
      }

      var payload = { product_type: spec.product_type, variant_id: variantId, quantity: 1, spec: spec };
      return computeHmacHex(payload, nonce)
        .then(function (signature) {
          return fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_type: payload.product_type,
              variant_id: payload.variant_id,
              quantity: payload.quantity,
              spec: payload.spec,
              nonce: nonce,
              signature: signature
            })
          });
        })
        .then(function (res) { return res.json().then(function (data) { return { res: res, data: data }; }); })
        .then(function (out) {
          if (!out.res.ok || !out.data.invoiceUrl) throw new Error(out.data.error || ('HTTP ' + out.res.status));
          clearPersistedArtwork();
          window.location.assign(out.data.invoiceUrl);
        });
    }

    function computeHmacHex(payload, nonceHex) {
      var enc = new TextEncoder();
      return crypto.subtle.importKey('raw', enc.encode(nonceHex), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
        .then(function (key) { return crypto.subtle.sign('HMAC', key, enc.encode(JSON.stringify(payload))); })
        .then(function (sig) { return Array.from(new Uint8Array(sig)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join(''); });
    }
  }

  function initAll(scope) {
    var s = scope || document;
    var root = (s.matches && s.matches('[data-stk-blue]')) ? s : s.querySelector('[data-stk-blue]');
    if (root) initBlue(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { if (e && e.target) initAll(e.target); });
})();
